import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePopup } from '@/contexts/PopupContext';
import { serviceCategories } from '@/data/services';
import { ChevronDown, Menu, X, MessageCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function Header() {
  const { openPopup } = usePopup();
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const megaRef = useRef(null);
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => { setMegaOpen(false); setMobileOpen(false); }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => { clearTimeout(timeoutRef.current); setMegaOpen(true); };
  const handleMouseLeave = () => { timeoutRef.current = setTimeout(() => setMegaOpen(false), 200); };

  useEffect(() => {
    const handleClick = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        setMegaOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const leftCategories = serviceCategories.slice(0, 2);
  const centerCategories = serviceCategories.slice(2, 4);
  const rightCategories = serviceCategories.slice(4, 6);

  return (
    <header className={`fixed left-0 right-0 z-50 glass border-b border-black/5 ${scrolled ? 'mynav' : 'top-8'}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="shrink-0" data-testid="header-logo">
            <img src="/assets/img/logo-color.png" alt="Trademark Pact" className="h-8 sm:h-12 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <Link to="/" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#064E3B] transition-colors rounded-md hover:bg-emerald-50" data-testid="nav-home">
              Home
            </Link>
            <Link to="/about" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#064E3B] transition-colors rounded-md hover:bg-emerald-50" data-testid="nav-about">
              About Us
            </Link>
            <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <button
                ref={triggerRef}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md flex items-center gap-1 ${megaOpen ? 'text-[#064E3B] bg-emerald-50' : 'text-slate-700 hover:text-[#064E3B] hover:bg-emerald-50'}`}
                data-testid="nav-services-trigger"
                onClick={() => setMegaOpen(!megaOpen)}
              >
                Services
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${megaOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            <Link to="/contact" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#064E3B] transition-colors rounded-md hover:bg-emerald-50" data-testid="nav-contact">
              Contact Us
            </Link>
          </nav>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => openPopup()}
              className="hidden sm:block bg-[#064E3B] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#065F46] transition-colors"
              data-testid="header-register-btn"
            >
              Register My Trademark
            </button>
            <button
              className="js-chat border border-[#064E3B] text-[#064E3B] px-4 py-2 rounded-full text-sm font-medium hover:bg-emerald-50 transition-colors flex items-center gap-2"
              data-testid="header-chat-btn"
              id="chat-now-btn-header"
            >
              <MessageCircle className="w-4 h-4" />
              Chat Now
            </button>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 text-slate-700" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] p-0">
              <SheetHeader className="p-4 border-b">
                <SheetTitle className="font-heading text-[#064E3B]">Trademark Pact</SheetTitle>
              </SheetHeader>
              <ScrollArea className="h-[calc(100vh-140px)]">
                <div className="p-4 space-y-1">
                  <Link to="/" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-emerald-50 rounded-md" data-testid="mobile-nav-home">Home</Link>
                  <Link to="/about" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-emerald-50 rounded-md" data-testid="mobile-nav-about">About Us</Link>
                  {serviceCategories.map((cat) => (
                    <div key={cat.id}>
                      <button
                        onClick={() => setMobileExpanded(mobileExpanded === cat.id ? null : cat.id)}
                        className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-emerald-50 rounded-md"
                        data-testid={`mobile-nav-${cat.id}`}
                      >
                        {cat.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === cat.id ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileExpanded === cat.id && (
                        <div className="ml-4 space-y-0.5 mt-1">
                          {cat.services.map((svc) => (
                            <Link
                              key={svc.slug}
                              to={`/services/${svc.slug}`}
                              onClick={() => setMobileOpen(false)}
                              className="block px-3 py-2 text-sm text-slate-600 hover:text-[#064E3B] hover:bg-emerald-50 rounded-md"
                              data-testid={`mobile-svc-${svc.slug}`}
                            >
                              {svc.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <Link to="/contact" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-emerald-50 rounded-md" data-testid="mobile-nav-contact">Contact Us</Link>
                </div>
                <div className="p-4 space-y-3 border-t">
                  <button onClick={() => { openPopup(); setMobileOpen(false); }} className="w-full bg-[#064E3B] text-white py-2.5 rounded-full text-sm font-medium" data-testid="mobile-register-btn">
                    Register My Trademark
                  </button>
                  <button className="w-full border border-[#064E3B] text-[#064E3B] py-2.5 rounded-full text-sm font-medium flex items-center justify-center gap-2" data-testid="mobile-chat-btn" id="chat-now-btn-mobile">
                    <MessageCircle className="w-4 h-4" /> Chat Now
                  </button>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Mega Menu */}
      {megaOpen && (
        <>
          <div
            className="fixed inset-0 top-[72px] z-40"
            onClick={() => setMegaOpen(false)}
            data-testid="mega-menu-backdrop"
          />
          <div
            ref={megaRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="absolute top-full left-0 right-0 bg-white border-t shadow-xl mega-menu-enter z-50"
            data-testid="mega-menu"
          >
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="grid grid-cols-3 gap-8">
              {/* Column 1 */}
              <div className="space-y-6">
                {leftCategories.map((cat) => (
                  <MegaCategory key={cat.id} category={cat} onClose={() => setMegaOpen(false)} />
                ))}
              </div>
              {/* Column 2 */}
              <div className="space-y-6">
                {centerCategories.map((cat) => (
                  <MegaCategory key={cat.id} category={cat} onClose={() => setMegaOpen(false)} />
                ))}
              </div>
              {/* Column 3 */}
              <div className="space-y-6">
                {rightCategories.map((cat) => (
                  <MegaCategory key={cat.id} category={cat} onClose={() => setMegaOpen(false)} scrollable={cat.services.length > 6} />
                ))}
              </div>
            </div>
          </div>
          </div>
        </>
      )}
    </header>
  );
}

function MegaCategory({ category, onClose, scrollable }) {
  const Icon = category.icon;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#10B981]" />
        <h3 className="font-heading font-semibold text-sm text-[#064E3B] uppercase tracking-wide">{category.name}</h3>
      </div>
      <div className={scrollable ? 'max-h-[200px] overflow-y-auto pr-2 hide-scrollbar' : ''}>
        <div className="space-y-0.5">
          {category.services.map((svc) => (
            <Link
              key={svc.slug}
              to={`/services/${svc.slug}`}
              onClick={onClose}
              className="block px-2 py-1.5 text-sm text-slate-600 hover:text-[#064E3B] hover:bg-emerald-50 rounded transition-colors"
              data-testid={`mega-svc-${svc.slug}`}
            >
              {svc.title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
